// /src/pages/Dashboard.jsx
import React, { useEffect, useMemo, useState } from 'react';
import Card from '../components/common/Card';
import { supabase } from '../supabaseClient';

// Reuse the same hooks/utilities you already use in ReportIntake.jsx
import {
  useIntakes,
  useDefaultIntakeId,
  useSubjects,
  useMatrixData,
  aggregateEnrollmentsToMatrix,
} from '../hooks/useReportIntake';

export default function Dashboard() {
  const [selectedIntakeId, setSelectedIntakeId] = useState('');

  // Intakes & default selection (same behavior as ReportIntake)
  const {
    data: intakes = [],
    isLoading: isLoadingIntakes,
    isError: isErrorIntakes,
    error: intakesError,
  } = useIntakes();
  const defaultIntakeId = useDefaultIntakeId(intakes);

  useEffect(() => {
    if (!selectedIntakeId && !isLoadingIntakes && intakes.length > 0 && defaultIntakeId) {
      setSelectedIntakeId(defaultIntakeId);
    }
  }, [selectedIntakeId, isLoadingIntakes, intakes, defaultIntakeId]);

  const intakeName = useMemo(
    () => intakes.find((i) => i.id === selectedIntakeId)?.name || '',
    [intakes, selectedIntakeId]
  );

  // Subjects (for labels and to build matrix columns)
  const {
    data: subjects = [],
    isLoading: isLoadingSubjects,
    isError: isErrorSubjects,
    error: subjectsError,
  } = useSubjects();

  const subjectCodes = useMemo(
    () => subjects.map((s) => s.code).sort((a, b) => String(a).localeCompare(String(b))),
    [subjects]
  );

  // Matrix = one row per student for the intake
  const {
    data: matrixRaw = [],
    isLoading: isLoadingMatrix,
    isError: isErrorMatrix,
    error: matrixError,
  } = useMatrixData(selectedIntakeId || null);

  const matrixRows = useMemo(() => {
    if (!selectedIntakeId) return [];
    return aggregateEnrollmentsToMatrix({
      rows: matrixRaw,
      subjectCodes,
    });
  }, [matrixRaw, subjectCodes, selectedIntakeId]);

  // --- Unique total students for the intake
  const uniqueStudents = matrixRows.length;

  // --- Counts per subject (number of students enrolled in each subject)
  const perSubject = useMemo(() => {
    // Build { code, name, count } for all subjects (including zero counts)
    const byCode = new Map(subjects.map((s) => [s.code, { code: s.code, name: s.name, count: 0 }]));
    for (const row of matrixRows) {
      // row.subjectFlags.{PS5001,...} = true/false
      for (const code of subjectCodes) {
        if (row.subjectFlags?.[code]) {
          const item = byCode.get(code);
          if (item) item.count += 1;
        }
      }
    }
    return Array.from(byCode.values()).sort((a, b) =>
      String(a.code).localeCompare(String(b.code))
    );
  }, [matrixRows, subjects, subjectCodes]);

  const maxCount = useMemo(
    () => (perSubject.length ? Math.max(...perSubject.map((r) => r.count)) : 0),
    [perSubject]
  );

  const anyLoading = isLoadingIntakes || isLoadingSubjects || isLoadingMatrix;
  const anyError = isErrorIntakes || isErrorSubjects || isErrorMatrix;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
      </div>

      {/* Compact toolbar for Intake selection */}
      <div className="rounded-xl border border-slate-200 bg-white/60 backdrop-blur-sm p-3 shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700">
              Intake
            </span>
            <span className="text-sm text-slate-700">
              {intakeName || '—'}
            </span>
          </div>
          <div className="w-full sm:w-56">
            <label htmlFor="intake" className="sr-only">Intake</label>
            <select
              id="intake"
              value={selectedIntakeId}
              onChange={(e) => setSelectedIntakeId(e.target.value)}
              className="block w-full rounded-md border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              disabled={isLoadingIntakes || isErrorIntakes}
            >
              {isLoadingIntakes && <option>Loading…</option>}
              {!isLoadingIntakes && !intakes.length && <option>No intakes found</option>}
              {!isLoadingIntakes &&
                intakes.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
            </select>
            {isErrorIntakes && (
              <p className="mt-1 text-xs text-red-600">
                Failed to load intakes: {intakesError?.message || 'Unknown error'}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Students per Subject */}
      <Card>
        <div className="flex items-center justify-between px-4 pt-4">
          <h2 className="text-base font-semibold text-slate-900">Students per Subject</h2>
          <div className="text-sm text-slate-600">
            Intake: <strong>{intakeName || '-'}</strong>
            <span className="mx-2">•</span>
            Enrolled Students: <strong>{uniqueStudents}</strong>
          </div>
        </div>

        <div className="p-4">
          {anyError && (
            <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              {isErrorSubjects
                ? `Failed to load subjects: ${subjectsError?.message || 'Unknown error'}`
                : isErrorMatrix
                ? `Failed to load data: ${matrixError?.message || 'Unknown error'}`
                : `Failed to load intakes: ${intakesError?.message || 'Unknown error'}`}
            </div>
          )}

          {anyLoading ? (
            <div className="py-10 text-center text-gray-500">Loading…</div>
          ) : !selectedIntakeId ? (
            <div className="py-10 text-center text-gray-500">Select an intake to view data.</div>
          ) : perSubject.length === 0 ? (
            <div className="py-10 text-center text-gray-500">No enrollments found for this intake.</div>
          ) : (
            <>
              <div className="mb-3 text-sm text-slate-600">{perSubject.length} subjects</div>
              <ul className="space-y-3">
                {perSubject.map((r) => {
                  const pct = maxCount ? Math.max(4, Math.round((r.count / maxCount) * 100)) : 0; // keep tiny bar visible
                  return (
                    <li key={r.code} className="group">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-baseline gap-2">
                          <span className="font-medium text-slate-900">{r.code}</span>
                          {r.name ? (
                            <span className="text-xs text-slate-500 group-hover:text-slate-600 transition-colors">
                              — {r.name}
                            </span>
                          ) : null}
                        </div>
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-700">
                          {r.count}
                        </span>
                      </div>
                      <div className="h-3 w-full rounded-md bg-slate-100">
                        <div
                          className="h-3 rounded-md shadow-sm transition-all duration-500 bg-gradient-to-r from-indigo-500 via-blue-500 to-sky-400"
                          style={{ width: `${pct}%` }}
                          aria-label={`${r.code} ${r.count} students`}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
